import { useState } from "react";
import { usePools } from "../hooks/usePools";
import { useAuth } from "../contexts/AuthContext";
import { can } from "../lib/permissions";
import { CreatePoolDialog } from "../components/CreatePoolDialog";
import { Link } from "react-router-dom";
import { ArrowUpRight, Users, Plus } from "lucide-react";
import { UserAvatar } from "../components/UserAvatar";
import { resolveMemberColor } from "../lib/memberColors";
import { ContentCenter, PageShell } from "../components/EmptyStateWrapper";
import { ShellContentFrame, ShellStatePane } from "../components/ShellContentFrame";
import { formatCurrency } from "../contexts/FxContext";
import { DEFAULT_CURRENCY } from "../lib/defaultCurrency";

const PAGE_HEADER = (
    <>
        <p className="label-eyebrow">Workspace</p>
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tight mt-1">Pools</h1>
        <p className="text-white/50 mt-1.5">Group revenue & recipients into pools. Run independent schedules per pool.</p>
    </>
);

export function MyPoolsPage() {
    const { activeOrg } = useAuth();
    const { pools, loading, refetch, selectPool } = usePools();
    const [createOpen, setCreateOpen] = useState(false);
    const role = activeOrg?.role || "member";
    const poolList = pools || [];
    const showLoadingReserve = loading;
    const showEmpty = !loading && poolList.length === 0;
    const showGrid = !loading && poolList.length > 0;

    return (
        <>
            <PageShell className="flex min-h-0 flex-1 flex-col">
                <div className="shrink-0 mb-6">
                    {showGrid ? (
                        <div className="flex items-end justify-between flex-wrap gap-3">
                            <div>{PAGE_HEADER}</div>
                            {can.createPool(role) && (
                                <button onClick={() => setCreateOpen(true)} className="btn-neon rounded-md px-4 py-2 text-sm inline-flex items-center gap-2" data-testid="create-pool-cta">
                                    <Plus className="h-4 w-4" /> New pool
                                </button>
                            )}
                        </div>
                    ) : (
                        <div>{PAGE_HEADER}</div>
                    )}
                </div>

                <ShellContentFrame
                    loading={loading}
                    loadingLabel="Loading pools…"
                    layout="stack"
                    minHeight="min-h-[calc(100dvh-14rem)]"
                    className="flex-1 min-h-0"
                    testId="pools-content-frame"
                    loadingTestId="pools-loading"
                >
                    <ShellStatePane active={showLoadingReserve} testId="pools-loading-reserve">
                        <div className="min-h-[20rem]" aria-hidden="true" />
                    </ShellStatePane>

                    <ShellStatePane active={showEmpty} testId="pools-empty-pane">
                        <ContentCenter testId="pools-empty">
                            <div className="surface w-full max-w-lg p-10 text-center">
                                <p className="font-heading text-2xl">No pools yet</p>
                                <p className="text-white/45 mt-2">Create a pool to split revenue with your team.</p>
                                {can.createPool(role) && (
                                    <button onClick={() => setCreateOpen(true)} className="btn-neon mt-5 rounded-md px-4 py-2 text-sm" data-testid="pools-empty-create">
                                        Create pool
                                    </button>
                                )}
                            </div>
                        </ContentCenter>
                    </ShellStatePane>

                    <ShellStatePane active={showGrid} testId="pools-grid-pane">
                        <div className="absolute inset-0 overflow-auto p-2">
                            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 pt-1">
                                {poolList.map((p) => (
                                    <Link key={p.id} to="/dashboard" onClick={() => selectPool(p.id)} className="surface p-5 transition-all hover:-translate-y-0.5 hover:border-white/15 block group" data-testid={`pool-card-${p.id}`}>
                                        <div className="flex items-start justify-between">
                                            <div className="min-w-0">
                                                <p className="label-eyebrow text-[#00BF0D]">{p.status?.toUpperCase()}</p>
                                                <h3 className="font-heading text-xl mt-1.5 truncate">{p.name || "Unnamed pool"}</h3>
                                                {p.description && <p className="text-xs text-white/40 mt-1 line-clamp-1">{p.description}</p>}
                                            </div>
                                            <ArrowUpRight className="h-4 w-4 text-white/30 group-hover:text-[#00BF0D] transition-colors" />
                                        </div>
                                        <div className="mt-4 flex items-center -space-x-2">
                                            {(p.members || []).slice(0, 4).map((m) => (
                                                <UserAvatar key={m.id} name={m.name} avatarUrl={m.avatar_url} className="h-7 w-7 text-[10px] ring-2 ring-[#262626]" accentColor={resolveMemberColor(m.color)} />
                                            ))}
                                        </div>
                                        <div className="mt-4 flex items-center justify-between text-sm">
                                            <span className="text-white/45 inline-flex items-center gap-1.5">
                                                <Users className="h-3.5 w-3.5" /> {(p.members || []).length} · {p.cadence || "—"}
                                            </span>
                                            <span className="font-mono text-[#00BF0D] tabular-nums">
                                                {formatCurrency(p.total_revenue || 0, p.currency || DEFAULT_CURRENCY, { decimals: 2 })}
                                            </span>
                                        </div>
                                    </Link>
                                ))}
                                {can.createPool(role) && (
                                    <button onClick={() => setCreateOpen(true)} className="border-2 border-dashed border-white/[0.08] hover:border-[#00BF0D]/40 rounded-2xl p-5 flex items-center justify-center text-white/40 hover:text-[#00BF0D] transition-colors min-h-[180px]" data-testid="new-pool-card">
                                        <span className="inline-flex flex-col items-center gap-1"><Plus className="h-5 w-5" /> New pool</span>
                                    </button>
                                )}
                            </div>
                        </div>
                    </ShellStatePane>
                </ShellContentFrame>
            </PageShell>

            <CreatePoolDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={() => { setCreateOpen(false); refetch(); }} />
        </>
    );
}
